import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Separator } from '@/ui/separator'
import { Input } from '@/ui/input'
import { Label } from '@/ui/label'
import { Textarea } from '@/ui/textarea'
import { MaterialIcon } from '@/ui/material-icon'
import { RoleModule, rolesApi } from '@/features/role/api/roles.api'
import { rolesKeys } from '@/features/role/api/roles.keys'
import styles from './CreateRole.module.scss'
import { Checkbox } from '@radix-ui/react-checkbox'
import { cn } from '@/shared/lib/utils'

export interface PermissionModule {
    id: string
    name: string
}

interface CreateRoleProps {
    roleName: string
    roleDescription: string
    expanded: boolean
    modules: PermissionModule[]
    selectedModuleIds: string[]
    title?: string
    onRoleNameChange: (v: string) => void
    onRoleDescriptionChange: (v: string) => void
    onModuleChange: (moduleId: string, selected: boolean) => void
    onSelectAll?: (selected: boolean) => void
    onToggleExpand: () => void
    onModulesPayloadChange?: (modules: RoleModule[]) => void
    initialSelectedPermissionKeys?: string[]
}

export default function CreateRole({
    roleName,
    roleDescription,
    expanded,
    modules,
    selectedModuleIds,
    title = 'Create Role',
    onRoleNameChange,
    onRoleDescriptionChange,
    onModuleChange,
    onSelectAll,
    onModulesPayloadChange,
    initialSelectedPermissionKeys = [],
}: CreateRoleProps) {
    const [searchTerm, setSearchTerm] = useState('')
    const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({})
    const [expandedFeatures, setExpandedFeatures] = useState<Record<string, boolean>>({})
    const [selectedPermissionState, setSelectedPermissionState] = useState<Record<string, boolean>>({})
    const lastPayloadHashRef = useRef<string>('')
    const lastInitialKeysHashRef = useRef<string>('')

    const { data: modulesWithFeatures = [], isLoading: modulesFeaturesLoading } = useQuery({
        queryKey: rolesKeys.modulesWithFeatures(),
        queryFn: rolesApi.getModulesWithFeatures,
        staleTime: 5 * 60 * 1000,
    })

    const { data: permissions = [], isLoading: permissionsLoading } = useQuery({
        queryKey: rolesKeys.permissions(),
        queryFn: rolesApi.getPermissions,
        staleTime: 5 * 60 * 1000,
    })

    // 🔍 Filter modules
    const filteredModules = useMemo(
        () =>
            modules.filter(module =>
                module.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
            ),
        [modules, searchTerm]
    )

    const selectedModuleSet = useMemo(
        () => new Set(selectedModuleIds),
        [selectedModuleIds]
    )

    const selectedModulesWithPermissions = useMemo(
        () =>
            modulesWithFeatures
                .filter((module) => selectedModuleSet.has(String(module.module_id)))
                .map((module) => ({
                    id: String(module.module_id),
                    name: module.label,
                    features: module.features.map((feature) => ({
                        id: `${module.module_id}-${feature.feature_id}`,
                        featureId: feature.feature_id,
                        name: feature.label,
                        permissions: permissions.map((permission) => ({
                            id: `${feature.feature_id}-${permission.permission_id}`,
                            permissionId: permission.permission_id,
                            name: permission.label,
                            description: permission.description,
                        })),
                    })),
                })),
        [modulesWithFeatures, permissions, selectedModuleSet]
    )

    const allCollapsed =
        selectedModulesWithPermissions.length > 0 &&
        selectedModulesWithPermissions.every(
            (module) => (expandedModules[module.id] ?? true) === false
        )

    // ✅ Select All state
    const allVisibleSelected =
        filteredModules.length > 0 &&
        filteredModules.every(module => selectedModuleIds.includes(module.id))

    // 🔁 Handle Select All
    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.checked

        if (onSelectAll) {
            onSelectAll(selected)
        }

        filteredModules.forEach(module => {
            onModuleChange(module.id, selected)
        })
    }

    const toggleModule = (moduleId: string) => {
        setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }))
    }

    const toggleFeature = (featureKey: string) => {
        setExpandedFeatures((prev) => ({ ...prev, [featureKey]: !prev[featureKey] }))
    }

    const isPermissionChecked = (permissionId: string) => !!selectedPermissionState[permissionId]

    const isFeatureChecked = (
        feature: { permissions: Array<{ id: string }> }
    ) => feature.permissions.length > 0 && feature.permissions.every((permission) => isPermissionChecked(permission.id))

    const isModuleChecked = (
        module: { features: Array<{ permissions: Array<{ id: string }> }> }
    ) => module.features.length > 0 && module.features.every((feature) => isFeatureChecked(feature))

    const handleModulePermissionToggle = (
        module: { features: Array<{ permissions: Array<{ id: string }> }> },
        checked: boolean
    ) => {
        setSelectedPermissionState((prev) => {
            const next = { ...prev }
            module.features.forEach((feature) => {
                feature.permissions.forEach((permission) => {
                    next[permission.id] = checked
                })
            })
            return next
        })
    }

    const handleFeaturePermissionToggle = (
        feature: { permissions: Array<{ id: string }> },
        checked: boolean
    ) => {
        setSelectedPermissionState((prev) => {
            const next = { ...prev }
            feature.permissions.forEach((permission) => {
                next[permission.id] = checked
            })
            return next
        })
    }

    const handleSinglePermissionToggle = (permissionId: string, checked: boolean) => {
        setSelectedPermissionState((prev) => ({
            ...prev,
            [permissionId]: checked,
        }))
    }

    const handleCollapseAll = () => {
        const shouldExpand = allCollapsed
        const moduleState: Record<string, boolean> = {}
        const featureState: Record<string, boolean> = {}

        selectedModulesWithPermissions.forEach((module) => {
            moduleState[module.id] = shouldExpand
            module.features.forEach((feature) => {
                featureState[feature.id] = shouldExpand
            })
        })

        setExpandedModules(moduleState)
        setExpandedFeatures(featureState)
    }

    const modulesPayload = useMemo<RoleModule[]>(
        () =>
            selectedModulesWithPermissions
                .map((module) => ({
                    module_id: Number(module.id),
                    features: module.features
                        .map((feature) => ({
                            feature_id: feature.featureId,
                            permissions: feature.permissions
                                .filter((permission) =>
                                    isPermissionChecked(permission.id)
                                )
                                .map((permission) => ({
                                    permission_id: permission.permissionId,
                                    is_allowed: true,
                                })),
                        }))
                        .filter((feature) => feature.permissions.length > 0),
                }))
                .filter((module) => module.features.length > 0),
        [selectedModulesWithPermissions, selectedPermissionState]
    )

    useEffect(() => {
        // While module/permission queries are still loading, `selectedModulesWithPermissions`
        // can be temporarily empty and we'd wipe the initial edit-mode selection.
        if (modulesFeaturesLoading || permissionsLoading) return
        if (selectedModulesWithPermissions.length === 0) return

        const validPermissionIds = new Set(
            selectedModulesWithPermissions.flatMap((module) =>
                module.features.flatMap((feature) =>
                    feature.permissions.map((permission) => permission.id)
                )
            )
        )

        setSelectedPermissionState((prev) => {
            const next: Record<string, boolean> = {}
            let changed = false

            Object.entries(prev).forEach(([key, value]) => {
                if (validPermissionIds.has(key)) {
                    next[key] = value
                } else {
                    changed = true
                }
            })

            return changed ? next : prev
        })
    }, [
        selectedModulesWithPermissions,
        modulesFeaturesLoading,
        permissionsLoading,
    ])

    useEffect(() => {
        if (!onModulesPayloadChange) return

        const payloadHash = JSON.stringify(modulesPayload)
        if (payloadHash === lastPayloadHashRef.current) return

        lastPayloadHashRef.current = payloadHash
        onModulesPayloadChange(modulesPayload)
    }, [modulesPayload, onModulesPayloadChange])

    useEffect(() => {
        const keysHash = initialSelectedPermissionKeys.join('|')
        if (!keysHash || keysHash === lastInitialKeysHashRef.current) return
        lastInitialKeysHashRef.current = keysHash

        const nextState: Record<string, boolean> = {}
        initialSelectedPermissionKeys.forEach((key) => {
            nextState[key] = true
        })
        setSelectedPermissionState(nextState)
    }, [initialSelectedPermissionKeys])

    return (
        <div
            className={styles.createRoleWrapper}
            data-expanded={expanded ? 'true' : 'false'}
        >
            {/* HEADER */}

            {/* FORM SECTION */}
            <div className={styles.cardBody}>
                <Separator />
                <div className={styles.cardHeader}>
                    <h1 className={styles.title}>{title}</h1>
                </div>
                <Separator />
                <div className={styles.formField}>
                    <Label className={styles.fieldLabel}>Role Name</Label>
                    <Input
                        type="text"
                        value={roleName}
                        onChange={e => onRoleNameChange(e.target.value)}
                        placeholder="Enter role name"
                        className={styles.fieldInput}
                    />
                </div>

                <div className={styles.formField}>
                    <Label className={styles.fieldLabel}>Role Description</Label>
                 <Textarea
                     className={cn(styles.textfield, "bg-[#F9FAFB]")}
                     value={roleDescription}
                     onChange={e => onRoleDescriptionChange(e.target.value)}
                     placeholder="Enter role description"
                     rows={4}
/>
                </div>
            </div>

            {/* MODULE ASSIGNMENT SECTION */}
            <div className={styles.assignModulesCard}>
                <Separator />

                <div className={styles.header}>
                    <div className={styles.cardHeader}>
                        <h2 className={styles.title}>
                            Assign Modules to this role
                        </h2>
                    </div>

                   <div className={styles.searchBox}>
                        <MaterialIcon name="search" size={16} />
                        <Input
                            type="text"
                            placeholder="Search Module"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className={cn(
                                styles.searchInput,
                                "focus-visible:ring-0 focus-visible:ring-offset-0"
                            )}
                        />
                    </div>
                </div>

                <Separator />

                <div className={styles.moduleList}>
                    <label className={styles.selectAllLabel}>
                        <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={handleSelectAll}
                        />
                        Select All
                    </label>

                    {filteredModules.length === 0 ? (
                        <div className={styles.noModules}>No modules found</div>
                    ) : (
                        filteredModules.map(module => (
                            <label className={styles.moduleItem} key={module.id}>
                                <input
                                    className={styles.checkbox}
                                    type="checkbox"
                                    color='#155DFC'
                                    checked={selectedModuleIds.includes(module.id)}
                                    onChange={e =>
                                        onModuleChange(module.id, e.target.checked)
                                    }
                                />
                                <span>{module.name.charAt(0).toUpperCase()+module.name.slice(1)}</span>
                            </label>
                        ))
                    )}
                </div>
            </div>

            <div className={styles.selectedModulesPermissions}>
                <Separator />
                <div className={styles.cardHeader}>
                    <h2 className={styles.title}>Select Module Feature Permissions</h2>
                    <button type="button" className={styles.collapseAllBtn} onClick={handleCollapseAll}>
                        <MaterialIcon name={allCollapsed ? 'expand_more' : 'expand_less'} size={16} />
                        {allCollapsed ? 'Expand All' : 'Collapse All'}
                    </button>
                </div>
                <Separator />

                <p className={styles.permissionsHint}>
                    Select entire module with all its features and permissions or expand to
                    select individual features and permissions within the module
                </p>

                {modulesFeaturesLoading || permissionsLoading ? (
                    <div className={styles.noModules}>Loading module feature permissions...</div>
                ) : selectedModulesWithPermissions.length === 0 ? (
                    <div className={styles.noModules}>
                        Select modules above to load module feature permissions
                    </div>
                ) : (
                    <div className={styles.permissionsModulesList}>
                        {selectedModulesWithPermissions.map((module) => {
                            const moduleOpen = expandedModules[module.id] ?? true
                            return (
                                <div key={module.id} className={styles.permissionsModule}>
                                    <button
                                        type="button"
                                        className={styles.permissionsModuleHeader}
                                        onClick={() => toggleModule(module.id)}
                                    >
                                        <div className={styles.permissionsModuleLeft}>
                                            <input
                                               
                                                type="checkbox"
                                                checked={isModuleChecked(module)}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) =>
                                                    handleModulePermissionToggle(module, e.target.checked)
                                                }
                                            />
                                            <span className={styles.permissionsModuleName}>{module.name}</span>
                                        </div>
                                        <MaterialIcon
                                            name={moduleOpen ? 'expand_more' : 'chevron_right'}
                                            size={18}
                                            className={styles.permissionsChevron}
                                        />
                                    </button>

                                    {moduleOpen && (
                                        <div className={styles.permissionsModuleBody}>
                                            {module.features.map((feature) => {
                                                const featureOpen = expandedFeatures[feature.id] ?? true
                                                return (
                                                    <div key={feature.id} className={styles.permissionsFeature}>
                                                        <button
                                                            type="button"
                                                            className={styles.permissionsFeatureHeader}
                                                            onClick={() => toggleFeature(feature.id)}
                                                        >
                                                            <div className={styles.permissionsFeatureLeft}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isFeatureChecked(feature)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) =>
                                                                        handleFeaturePermissionToggle(feature, e.target.checked)
                                                                    }
                                                                />
                                                                <span className={styles.permissionsFeatureName}>{feature.name}</span>
                                                            </div>
                                                            <MaterialIcon
                                                                name={featureOpen ? 'expand_more' : 'chevron_right'}
                                                                size={18}
                                                                className={styles.permissionsChevron}
                                                            />
                                                        </button>

                                                        {featureOpen && (
                                                            <div className={styles.permissionsFeatureBody}>
                                                                <div className={styles.permissionsLabel}>Permissions</div>
                                                                <div className={styles.permissionsGrid}>
                                                                    {feature.permissions.map((permission) => (
                                                                        <label key={permission.id} className={styles.permissionItem}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isPermissionChecked(permission.id)}
                                                                                onChange={(e) =>
                                                                                    handleSinglePermissionToggle(
                                                                                        permission.id,
                                                                                        e.target.checked
                                                                                    )
                                                                                }
                                                                            />
                                                                            <div className={styles.permissionText}>
                                                                                <div className={styles.permissionName}>{permission.name}</div>
                                                                                <div className={styles.permissionDesc}>{permission.description}</div>
                                                                            </div>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}